<template>
  <div class="flex items-start">
    <div class="flex flex-col items-center">
      <Avatar
        :size="80"
        :url="previewFile(org.currentOrganization?.logo)"
        :name="org.currentOrganization?.organizationName"
      />
      <a-button @click="selectAvatar" type="text" class="mt-4 mx-auto text-center">编辑</a-button>
    </div>
    <div class="flex-1 pl-[32px]">
      <div style="color: rgba(0, 0, 0, 0.5); margin-bottom: 16px">
        {{ org.currentOrganization.organizationName }} · {{ dataState.rows?.length }}位成员
      </div>
      <template v-for="(m, i) in dataState.rows" :key="m.id">
        <TableItemNew
          :member="m"
          @member-change="e => (dataState.rows[i] = e)"
          @set-role="p => handleSetRole(m, p)"
          @remove-user="handleRemoveUser(m)"
          @exit-org="handleExitOrg(m)"
        ></TableItemNew>
      </template>
    </div>
  </div>
  <InviteModal
    :visible="inviteVisible"
    @close="handleInviteVisible(false)"
    :inviteToken="inviteToken"
  />
  <PermissionManager
    v-model:visible="selectorVisible"
    title="调整项目权限"
    :isMember="true"
  ></PermissionManager>
</template>
<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue'
import { Avatar, Modal } from '@elecmap/elecmap-ui'
import type { IKeywords } from '@elecmap/elecmap-ui/lib/form/types'
import { omit, pick } from 'lodash'
import { useRoute } from 'vue-router'
import {
  getMemberList,
  generateInvitedToken,
  deleteEmployee,
  exitOrganization,
  updateOrganizationLogo,
} from '@elecmap/api/src/ram'
import type { EmployeePageQuery } from '@elecmap/api/src/ram.d'
import InviteModal from '@elecmap/shared/src/components/InviteModal.vue'
import PermissionManager from '../member-manage/permission-adjust.vue'
import { useRoleList } from '../member-manage/useRoleList'
import { useOrganization } from '@/store/organization'
import { previewFile } from '@elecmap/api/src/cad/file'
import { selectFile } from '@elecmap/shared/src/utils/dom'
import TableItemNew from '@/components/MemberTable/table-item-new.vue'

const route = useRoute()
const isInPlatform = route.path.startsWith('/platform')

const inviteVisible = ref(false)
const inviteToken = ref()
const org = useOrganization()

const selectorVisible = ref(false)

const { updateRoleDirectly } = useRoleList('团队成员')

const dataState = reactive<{ rows: EmployeePageQuery[] }>({ rows: [] })

const activeState = reactive<{
  departmentId: number | null
  staff: EmployeePageQuery | null
}>({
  departmentId: null,
  staff: null,
})

const paginationState = reactive({
  pageIndex: 1,
  pageSize: 100,
  totalCount: 0,
})

const queryState = reactive<{ keywords: IKeywords }>({
  keywords: {},
})

const handleInviteVisible = (visible: boolean) => {
  inviteVisible.value = visible
  if (visible) {
    generateInvitedToken().then((res: any) => {
      inviteToken.value = res
    })
  }
}

const queryRequest = () => {
  const queryData = {
    departmentId: activeState.departmentId || undefined,
    ...omit(queryState.keywords, '_searchOptions'),
    ...pick(paginationState, 'pageIndex', 'pageSize'),
  }
  getMemberList(queryData).then(data => {
    dataState.rows = data.data || []
    paginationState.totalCount = data.totalCount
  })
}

async function selectAvatar() {
  const f = await selectFile()
  await updateOrganizationLogo({ logo: f[0].uuid })
  org.currentOrganization.logo = f[0].uuid
}

function handleSetRole(m: EmployeePageQuery, p: string) {
  updateRoleDirectly(m.userId, +p).then(() => {
    queryRequest()
  })
}

function selectMember(m: EmployeePageQuery) {
  return new Promise<number>(resolve => {
    Modal.form({
      width: 500,
      title: '请选择交接成员',
      closable: false,
      okText: '确定',
      formItems: [
        {
          label: '交接成员',
          placeholder: '请选择交接成员',
          required: true,
          type: 'Select',
          key: 'receiverEmployeeId',
          options: dataState.rows
            .filter(r => r.id !== m.id)
            .map(r => ({ id: r.id, name: r.employeeName })),
          props: {
            getPopupContainer: () => document.body,
          },
        },
      ],
      formState: reactive({ keywords: { receiverEmployeeId: null }, tags: {} }),
      onSubmit({ keywords }: any) {
        resolve(keywords.receiverEmployeeId)
      },
    })
  })
}

async function handleRemoveUser(m: EmployeePageQuery) {
  try {
    const receiverEmployeeId = await selectMember(m)
    await deleteEmployee({ id: m.id, receiverEmployeeId })
    queryRequest()
  } catch (e) {}
}

async function handleExitOrg(m: EmployeePageQuery) {
  const receiverEmployeeId = await selectMember(m)
  await exitOrganization({ receiverEmployeeId })
  await useOrganization().resetToPersonalOrg()
  window.location.href = '/#/workbench'
}

onMounted(() => {
  queryRequest()
})
</script>

<style lang="less">
.em-page {
  margin-top: 4px;

  .em-page-header {
    height: 26px;
  }
}

.manage-page {
  padding-right: 16px;
}
</style>
